import { authedFetch, UnauthorizedError } from '@apis/auth';

/**
 * Cumulative all-time stats for a single profession. Drives the Contractor
 * Analysis report's hero strip. Mirrors `ProfessionAllTimeStats` on the
 * backend port.
 */
export interface IProfessionAllTimeStats {
  firstWorkingDayIso: string | null;
  totalWorkHours: number;
  avgDailyWorkers: number;
  avgDailyWorkHours: number;
}

/** Per-day worker + hours data point inside the analysis response. */
export interface IProfessionAnalysisDay {
  date: string;
  totalWorkHours: number;
  totalWorkers: number;
}

/** Workers-vs-hours chart payload for the requested window. */
export interface IProfessionAnalysis {
  averageDayWorkDurationHours: number;
  days: IProfessionAnalysisDay[];
}

export interface IProfessionLocationFloor {
  floorId: string;
  floorNick: string;
  totalHours: number;
}

export interface IProfessionLocationArea {
  areaId: string;
  areaNick: string;
  floorId: string;
  totalHours: number;
}

export interface IProfessionLocations {
  byFloor: IProfessionLocationFloor[];
  byArea: IProfessionLocationArea[];
}

/** Static project floor catalog row. */
export interface IProjectFloor {
  floorId: string;
  floorNick: string;
  shortFloorNick: string | null;
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

/**
 * Cumulative hero-card stats. Returns `null` on upstream 404 so the FE can
 * render an "no working data yet" empty state without conflating it with
 * network / auth failures.
 */
export async function fetchProfessionAllTimeStats(
  siteId: string,
  professionId: string,
  signal?: AbortSignal,
): Promise<IProfessionAllTimeStats | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/professions/${encodeURIComponent(professionId)}/all-time-stats`,
    { signal },
  );
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return (await res.json()) as IProfessionAllTimeStats;
}

/**
 * Per-day workers + hours across the requested window. `dateFrom`/`dateTo`
 * are project-tz YYYY-MM-DD strings.
 */
export async function fetchProfessionAnalysis(
  siteId: string,
  professionId: string,
  dateFrom: string,
  dateTo: string,
  signal?: AbortSignal,
): Promise<IProfessionAnalysis> {
  const qs = new URLSearchParams({ dateFrom, dateTo }).toString();
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/professions/${encodeURIComponent(professionId)}/analysis?${qs}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProfessionAnalysis;
}

/**
 * Per-floor / per-area time aggregation across the requested window —
 * Location Distribution panel.
 */
export async function fetchProfessionLocations(
  siteId: string,
  professionId: string,
  dateFrom: string,
  dateTo: string,
  signal?: AbortSignal,
): Promise<IProfessionLocations> {
  const qs = new URLSearchParams({ dateFrom, dateTo }).toString();
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/professions/${encodeURIComponent(professionId)}/locations?${qs}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProfessionLocations;
}

/**
 * Project floor catalog — used by the Location Distribution panel to
 * resolve floor IDs to canonical display nicks and to render rows in the
 * project's intended floor order.
 */
export async function fetchProjectFloors(
  siteId: string,
  signal?: AbortSignal,
): Promise<IProjectFloor[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/floors`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IProjectFloor[];
}
